/**
 * escapeShellDollars
 *
 * 把"看起来不是 LaTeX 公式分隔符"的 $ 字符做安全处理，避免 KaTeX /
 * markdown-it-katex 把诸如 `echo $PATH`、`$(date)`、`${VAR}`、`价格 $100`
 * 之类内容当成行内公式吃掉。
 *
 * 两种 mode：
 *   - mode='markdown'（默认，给 markdown-it 用）
 *       输出 `\$`。markdown-it 的 escape 规则在 markdown-it-katex 的 math_inline
 *       之前运行，会把 `\$` 消解为字面 `$`，最终 HTML 没有任何额外字符。
 *
 *   - mode='html'（给 v-latex 这种"先 innerHTML 再让 KaTeX auto-render 扫描"的
 *     场景用）
 *       输出 `<span class="vlsd">$</span>`。KaTeX 的 renderElem 只合并相邻
 *       的 *text* 兄弟节点（见 katex/contrib/auto-render/auto-render.ts），
 *       中间一插入 element 就会切断 text node 串，KaTeX 的 splitAtDelimiters
 *       从而无法把这个 $ 与其它 $ 配成对。浏览器照常显示 $ 字符，没有视觉副作用。
 *       注意：v-latex 指令需要把 'vlsd' 加入 ignoredClasses 做防御性兜底。
 *       直接写 `\$` 不行，因为 innerHTML 不解析 \ 转义、且 KaTeX 在寻找
 *       *开启* 分隔符时不检查前导 \，反而会让用户看到字面 "\$"。
 *
 * 设计原则（保守，倾向"不渲染"）：
 *  1. 代码区（<pre>/<code>/反引号/围栏代码块）以及 HTML 标签内部的 $ 一律不动；
 *  2. 已经写成 \$ 的转义 $ 一律不动；
 *  3. 双美元 $$...$$ 优先按块级公式判定：能配对、且内部含 LaTeX 特征字符才保留；
 *  4. 单美元 $...$ 优先按行内公式判定：能配对、且内部含 LaTeX 特征字符才保留；
 *     行内配对允许跨过单个软换行（AI 流式输出 / Word 粘贴常把公式拆到下一行），
 *     但遇到连续两次 \n（段落分隔）立即停手；
 *  5. 若已配对但无 LaTeX 特征，再看内部是否是"单字母数学变量"形态——也就是
 *     一个英文字母，可选再接 1 个数字或 1 个撇号（如 $P$、$V$、$x$、$x1$、$F'$）
 *     就按公式保留。范围故意收得很窄，避免误伤 $HOME$、$PATH$ 等多字母 Shell
 *     变量名，以及 $HOME/bin:$PATH 这种路径形态；
 *  6. 再看内部是否是"单字母变量逗号列表"形态——多个第 5 步那种单字母变量
 *     用逗号串起来（如 $B,C$、$A,D$、$x,y,z$、$a1,b2$）就按公式保留。
 *     专治线代题"$B,C$是否为零"、"$A,D$可逆"这种枚举写法；故意不放宽到
 *     多字母 token，避免把 $HOME,PATH$ 等 Shell 变量列表也救进来；
 *  7. 再看内部是否是"数学元组 / 元组列表"形态——
 *     单元组 $(x,y,z)$、$(a,b)$ 或多元组列表 $(1,0,0),(0,1,0),(0,0,1),(0,0,0)$，
 *     就按公式保留。专治几何题里的顶点坐标列表写法被 SHELL_TRIGGER 误伤的场景。
 *     故意要求每个元组内 *至少一个逗号*，把单参数的 $(date)$ / $(VAR)$ 这类
 *     命令置换形态留给 SHELL 分支转义。长度上限 120，单独成顶层规则，
 *     不受第 8 步"裸表达式" length ≤20 的限制；
 *  8. 再看内部是否是"裸表达式"形态——长度 ≤20，且含 = / < / > / | / + / - 之一，
 *     或形如 letter(...) 的函数调用（如 $Ax=b$、$r(A)=2$、$f(x)$、$2<3$、$|x|<1$、
 *     $n-1$、$x+y$）就按公式保留。专治题库里没写宏的真公式被 SHELL_TRIGGER
 *     误伤进而级联吃掉相邻 $\frac/\pi/...$ 公式的情况。
 *     故意不收 `*` 和 `/` —— 它们在 `$HOME/bin:$PATH`、`echo $* $@` 里太常见，
 *     收进来会把 Shell 命令整段误判为公式；
 *  9. 再做一道"段落上下文加成"——若当前 $ 所在段落已经出现明显 LaTeX 信号
 *     （\frac / \Sigma / \begin / ^{ / _{ 等），就对 2–6 字符"字母开头、后续
 *     字母/数字/撇号"的 token 也按公式保留（$xOy$/$yOz$/$zOx$/$AB$/$ABCD$）。
 *     非数学密集段这条豁免完全不生效，$HOME$/$PATH$ 等仍按 Shell 变量名转义。
 *     段落按"连续 ≥2 个 \n"切分，基于已屏蔽代码 / HTML 的 masked 文本，
 *     防止 <code> / 围栏代码块里的 \frac 把整段判定误染成数学密集；
 * 10. 否则——若紧跟 Shell 触发字符（[A-Za-z_({0-9?#@*$!-]）→ 转义当前 $；
 * 11. 否则——已配对但内部无任何公式特征（$中文$、$ 空格 $ 之类）→ 仍然
 *     *只转义当前 $*，把闭合 $ 留给下一轮判定。早期版本把闭合 $ 一并吞掉，
 *     但题库正文常见 "$foo$ 后面 $\frac{1}{6}$" 这种"非公式 $X$ + 真公式"
 *     混排，把闭合 $ 当对子吃掉会把下游真公式的开 $ 一起拖下水级联崩溃；
 * 12. 否则——落单的 $ → 转义。
 *
 * "LaTeX 特征字符"指 \、^、_、{、} 或常见 macro（\frac、\sqrt、希腊字母等）。
 * 真公式几乎必带这些字符；不含特征的成对 $...$ 在历史题库里多半是
 * "价格 $100, 优惠 $200" 之类的误用，但 $P$ 这种单变量、$Ax=b$ 这种裸表达式
 * 也是高频公式形态，故再加两层"短而干净"的豁免去把它们从误判里救回来。
 */

export type EscapeMode = 'markdown' | 'html'

const LATEX_FEATURE_RE = /[\\^_{}]|\\(?:frac|sqrt|sum|int|prod|lim|cdot|times|div|pm|mp|approx|leq|geq|neq|infty|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|vartheta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega|hat|bar|tilde|vec|dot|ddot|left|right|begin|end|mathbf|mathrm|mathcal|mathbb|text|operatorname|displaystyle|partial|nabla|forall|exists|in|notin|subset|supset|cup|cap)\b/

// 段落级"数学密集"判定信号：仅认带反斜杠的常见 LaTeX 宏 + 带花括号的上下标
// (^{...} / _{...})。故意比 LATEX_FEATURE_RE 收得更窄：单独的 { } ^ _ 不算信号，
// 避免把 markdown 标题里的 #、模板字符串 ${var}、对话框模板 {foo} 等无意中
// 当成"数学触发器"，进而把整段都放宽到允许多字母 token 豁免。
//
// 命中条件之一即视为"该段落是数学密集段"：
//  - 任一带反斜杠的常见 LaTeX 宏（\frac / \Sigma / \begin / \mathbf / ...）；
//  - 形如 ^{...} 或 _{...} 的带花括号上下标（裸 ^ / _ 仍不算）。
const MATH_RICH_SIGNAL_RE = /\\(?:frac|sqrt|sum|int|prod|lim|begin|end|cdot|times|div|pm|mp|approx|leq|geq|neq|infty|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|vartheta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega|partial|nabla|mathbf|mathrm|mathcal|mathbb|hat|bar|tilde|vec|dot|ddot|left|right|overrightarrow|overline|underline|operatorname|displaystyle|text|forall|exists)\b|\^\{|_\{/

const SHELL_TRIGGER_RE = /[A-Za-z_({0-9?#@*$!\-]/

// 单字母数学变量豁免：单个英文字母，可选再接 1 个数字或 1 个撇号。
// 覆盖 $P$、$V$、$G$、$x$、$y$、$F'$、$x1$ 这种被 SHELL_TRIGGER 误伤的常见公式。
//
// 故意写得很窄：先前更宽的版本（白名单含 `/` `:` `\s`、长度允许到 12）会把
// `$HOME/bin:$PATH` 里 `HOME/bin:` 当成公式、把 `$HOME$` `$PATH$` `$USER$` 这种
// 全大写 Shell 变量名当成公式，严重误伤包含 Shell 命令的 markdown 段落。
//
// 注意分工：这条只救"形如单变量"的最小形态；
//  - 含运算符 / 函数调用的"裸表达式"（$Ax=b$、$r(A)=2$、$2<3$、$n-1$）
//    由 looksLikeNakedMath 负责；
//  - "几个单字母变量逗号列表"（$B,C$、$A,D$、$x,y,z$）由 MATH_VAR_LIST_RE 负责。
// 三条规则正交互补，不要混在一起放宽。
const SIMPLE_MATH_INNER_RE = /^[A-Za-z](?:[0-9]|')?$/

// 数学变量列表豁免：以逗号分隔的"单字母（可选 1 数字或 1 撇号）"序列。
// 专治 $B,C$、$A,D$、$x,y$、$x,y,z$、$a1,b2$ 这种"几个变量"枚举写法 ——
// 数学题里非常高频（"$B,C$是否为零"、"$A,D$可逆"），缺反斜杠宏躲不过
// LATEX_FEATURE_RE，开头字母又让 SHELL_TRIGGER 误判为 Shell 命令，
// 一旦被吞会级联污染整段后续公式（包括 $\begin{bmatrix}...$ 这种长块）。
//
// 故意把每个 token 收得和 SIMPLE_MATH_INNER_RE 一样窄：单字母 + 可选 1 数字/撇号。
// 避免把 $HOME,PATH$ 这种全大写多字母 Shell 变量名列表也救进来。
const MATH_VAR_LIST_RE = /^[A-Za-z](?:[0-9]|')?(?:\s*,\s*[A-Za-z](?:[0-9]|')?)+$/

// 数学元组 / 元组列表豁免：单个 (a,b,...) 或多个用逗号串联的 (a,b),(c,d),(e,f)。
// 专治几何题里高频出现的"顶点坐标列表"写法：
//   $(1,0,0),(0,1,0),(0,0,1),(0,0,0)$  ——  四面体顶点
//   $(x,y,z)$ / $(a,b)$                  ——  单个坐标点 / 向量
//
// 之前单元组形态由 looksLikeNakedMath 处理，但那条有 length ≤ 20 的硬上限，
// 4 个三元组（33 字符）就过不了；这里独立成顶层规则，length 上限放宽到
// MATH_TUPLE_LIST_MAX = 120，覆盖 8 个二维点（≈53 字符）这种极限。
//
// 安全要点：
//  1) 每个元组内部必须 ≥1 个逗号（[^()\n]*,[^()\n]*），与 $(date)$ / $(uname)$
//     这类命令置换形态区分开 —— Shell 命令置换不会在小括号里塞逗号；
//  2) 不允许嵌套括号（[^()\n] 排除括号本身），结构清晰、无回溯灾难风险；
//  3) 不允许跨行；
//  4) 元组之间必须用逗号分隔，避免 $(a,b)(c,d)$ 这种贴在一起的非常规写法。
const MATH_TUPLE_LIST_RE = /^\([^()\n]*,[^()\n]*\)(?:\s*,\s*\([^()\n]*,[^()\n]*\))*$/
const MATH_TUPLE_LIST_MAX = 120

function looksLikeMathTupleList(s: string): boolean {
  if (s.length > MATH_TUPLE_LIST_MAX) return false
  return MATH_TUPLE_LIST_RE.test(s)
}

// "数学密集段专属"的短 token 豁免：2-6 字符、字母开头、后续允许字母 / 数字 / 撇号。
// 专治高数教材里 $xOy$、$yOz$、$zOx$ 坐标面记号，几何题里 $AB$、$ABCD$、$ABC$
// 多顶点 / 多边形记号，以及 $AB'$、$x12$ 这类带数字 / 撇号尾巴的多字母数学变量
// 被 SHELL_TRIGGER_RE 误吃的高频场景。
//
// 故意只在"段落已经出现明显 LaTeX 信号"（MATH_RICH_SIGNAL_RE 命中）时才启用：
//  - 在数学密集段内，认为该段已是数学语境，把 $HOME$ / $PATH$ 等全大写 Shell
//    变量名形态当成公式的风险换"高频教材记号能正常渲染"是值得的；
//  - 在非数学密集段（纯文档 / 纯 Shell 命令段）里，这条豁免完全不生效，
//    现有保守策略原样不变，$HOME$ / $PATH$ / $foo$ 仍按 Shell 变量名转义。
// 这条与上面五条豁免的关系：放在"逗号列表 / 元组 / 裸表达式"之后、SHELL_TRIGGER
// 之前作为"上下文加成"的最后一道救助。
const MATH_TOKEN_RICH_RE = /^[A-Za-z][A-Za-z0-9']{1,5}$/

// "裸表达式"豁免：短表达式 + 至少一个数学运算符 / 比较 / 绝对值 / 函数调用形态。
// 与 SIMPLE_MATH_INNER_RE 互补，专治题库里常见但缺反斜杠宏的真公式
// （$Ax=b$、$r(A)=2$、$Ax=0$、$f(x)$、$r(A)$、$2<3$、$a>0$、$|x|<1$、
// $n-1$、$x+y$）被 SHELL_TRIGGER 误伤后又级联吃掉相邻 $\frac/\pi/...$
// 公式的情况。
//
// 元组形态（$(x,y,z)$、$(1,0,0),(0,1,0),...$）已被独立的 MATH_TUPLE_LIST_RE
// 接管，不再在这里出现，避免长度上限互相打架。
//
// 设计要点（仍偏保守）：
//  - 长度上限 20，避免把整段非公式正文裹进来；
//  - 必须含 [=<>|+\-] 之一，或形如 letter(...) 的函数调用；
//  - 故意不收 `*` 和 `/` —— 它们在 Shell 路径 / glob 里太常见
//    （`$HOME/bin:$PATH`、`echo $* $@`），加进来会把 Shell 命令整段当公式
//    误吞，得不偿失。$1/2$、$2*3$ 这类极简表达式宁可走原始保守路径；
//  - 纯字母（$HOME$/$PATH$/$USER$）和纯数字（$100$/$200$）一律不救，
//    交回 SHELL 分支按 Shell 变量 / 价格转义。理论上 $5-10$ 这种价格区间
//    会被算入"含 - 的短裸表达式"误判为公式，但中文题库里几乎不会出现
//    `$5-10$` 形态的价格写法，权衡之下接受这点风险换 $n-1$ 这条高频公式。
function looksLikeNakedMath(inner: string): boolean {
  const s = inner.trim()
  if (!s || s.length > 20) return false
  if (/[=<>|+\-]/.test(s)) return true
  if (/[A-Za-z]\([^()\n]{0,10}\)/.test(s)) return true
  return false
}

/** v-latex 指令应把这个 class 加入 KaTeX 的 ignoredClasses 列表 */
export const SKIP_DOLLAR_CLASS = 'vlsd'

const HTML_DOLLAR_OPEN = `<span class="${SKIP_DOLLAR_CLASS}">`
const HTML_DOLLAR_CLOSE = '</span>'

const PLACEHOLDER_PREFIX = '\u0000ESDOL'
const PLACEHOLDER_SUFFIX = '\u0000'
const PLACEHOLDER_RE = /\u0000ESDOL(\d+)\u0000/g

function escapedDollar(mode: EscapeMode): string {
  return mode === 'html' ? `${HTML_DOLLAR_OPEN}$${HTML_DOLLAR_CLOSE}` : '\\$'
}

function isEscapedDollar(text: string, idx: number): boolean {
  let count = 0
  for (let p = idx - 1; p >= 0 && text[p] === '\\'; p--) count++
  return count % 2 === 1
}

function findInlinePair(text: string, from: number): number {
  // 允许行内 $...$ 跨过单个软换行（AI 流式输出 / Word 粘贴常见把公式拆到下一行），
  // 但只要出现"连续 \n"（段落分隔）就立刻停手，避免吞掉整段非公式正文。
  let sawNewline = false
  for (let k = from; k < text.length; k++) {
    const c = text[k]
    if (c === '\n') {
      if (sawNewline) return -1
      if (text[k + 1] === '\n') return -1
      sawNewline = true
      continue
    }
    if (c === '$') {
      if (isEscapedDollar(text, k)) continue
      // 紧贴另一个 $ 视为块级，不在行内配对
      if (text[k + 1] === '$') return -1
      return k
    }
  }
  return -1
}

function findBlockClosing(text: string, from: number): number {
  for (let k = from; k <= text.length - 2; k++) {
    if (text[k] === '$' && text[k + 1] === '$') {
      if (!isEscapedDollar(text, k)) return k
    }
  }
  return -1
}

function maskCodeRegions(input: string): { masked: string; masks: string[] } {
  const masks: string[] = []
  const place = (m: string) => {
    const i = masks.length
    masks.push(m)
    return `${PLACEHOLDER_PREFIX}${i}${PLACEHOLDER_SUFFIX}`
  }

  let masked = input
  // 顺序敏感：先吃围栏/块级，再吃行内/标签
  masked = masked.replace(/```[\s\S]*?```/g, place)
  masked = masked.replace(/<pre\b[^>]*>[\s\S]*?<\/pre>/gi, place)
  masked = masked.replace(/<code\b[^>]*>[\s\S]*?<\/code>/gi, place)
  masked = masked.replace(/`[^`\n]+`/g, place)
  // 屏蔽剩余 HTML 标签本身（避免 <a href="?p=$1"> 这类 attribute 被改写）
  masked = masked.replace(/<[^<>]+>/g, place)

  return { masked, masks }
}

function unmask(text: string, masks: string[]): string {
  return text.replace(PLACEHOLDER_RE, (full, idx) => {
    const i = Number(idx)
    return Number.isFinite(i) && i < masks.length ? masks[i] : full
  })
}

interface Segment {
  /** masked 文本上的起始下标（含） */
  start: number
  /** masked 文本上的结束下标（不含），段间无空隙 —— 段落分隔符（连续 \n）
   *  归入前一段，避免 i 落到任何"段表里没有的位置" */
  end: number
  /** 段落是否含明显 LaTeX 信号 */
  isMathRich: boolean
}

/**
 * 把 masked 文本按"连续 ≥2 个 \n"切成若干段，并对每段预先标记是否数学密集。
 * 划分粒度故意取段落级（而非全文级 / 全句级）：段落是 markdown 里最自然的
 * "上下文"单位，可以让一个含 Shell 命令的段落和另一个含数学公式的段落
 * 各走各的规则互不污染。
 *
 * 划分基于 masked 文本（已屏蔽代码 / HTML），保证 <code>$PATH</code> /
 * 围栏代码块里的 \frac 不会污染 isMathRich 判定。
 */
function buildSegments(masked: string): Segment[] {
  if (masked.length === 0) {
    return [{ start: 0, end: 0, isMathRich: false }]
  }
  const segments: Segment[] = []
  const splitRe = /\n{2,}/g
  let lastEnd = 0
  let m: RegExpExecArray | null
  while ((m = splitRe.exec(masked)) !== null) {
    const start = lastEnd
    const segText = masked.slice(start, m.index)
    const end = m.index + m[0].length
    segments.push({
      start,
      end,
      isMathRich: MATH_RICH_SIGNAL_RE.test(segText)
    })
    lastEnd = end
  }
  if (lastEnd < masked.length) {
    const segText = masked.slice(lastEnd)
    segments.push({
      start: lastEnd,
      end: masked.length,
      isMathRich: MATH_RICH_SIGNAL_RE.test(segText)
    })
  }
  return segments
}

export function escapeNonMathDollars(input: string, mode: EscapeMode = 'markdown'): string {
  if (!input) return ''
  if (input.indexOf('$') === -1) return input

  const ESC = escapedDollar(mode)

  const { masked, masks } = maskCodeRegions(input)
  const segments = buildSegments(masked)
  let segCursor = 0
  // 段表按起始下标单调递增，主循环里 i 也单调递增，所以这里用一个游标顺序推进
  // 即可，总开销 O(masked.length)。
  const isMathRichAt = (pos: number): boolean => {
    while (segCursor < segments.length - 1 && pos >= segments[segCursor].end) {
      segCursor++
    }
    return segments[segCursor].isMathRich
  }

  const out: string[] = []
  let i = 0
  const len = masked.length

  while (i < len) {
    const ch = masked[i]
    if (ch !== '$') {
      out.push(ch)
      i++
      continue
    }

    if (isEscapedDollar(masked, i)) {
      out.push(ch)
      i++
      continue
    }

    // $$ 块级
    if (masked[i + 1] === '$') {
      const close = findBlockClosing(masked, i + 2)
      if (close === -1) {
        out.push(ESC, ESC)
        i += 2
        continue
      }
      const inner = masked.slice(i + 2, close)
      if (LATEX_FEATURE_RE.test(inner)) {
        out.push('$$', inner, '$$')
      } else {
        out.push(ESC, ESC, inner, ESC, ESC)
      }
      i = close + 2
      continue
    }

    // 单 $：先尝试行内配对
    const close = findInlinePair(masked, i + 1)
    if (close !== -1) {
      const inner = masked.slice(i + 1, close)
      if (LATEX_FEATURE_RE.test(inner)) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      // 配对成功但内部无 LaTeX 特征：先豁免"单字母数学变量"形态（$P$、$x1$、$F'$）
      if (SIMPLE_MATH_INNER_RE.test(inner.trim())) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      // 再豁免"单字母变量逗号列表"形态（$B,C$、$A,D$、$x,y,z$、$a1,b2$）。
      // 这条专治题库里"$B,C$是否为零"、"$A,D$可逆"这种枚举写法被 SHELL_TRIGGER
      // 误伤后又级联吃掉 $\begin{bmatrix}...\end{bmatrix}$ 这种长块公式的场景。
      if (MATH_VAR_LIST_RE.test(inner.trim())) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      // 再豁免"数学元组 / 元组列表"形态（$(x,y,z)$、$(a,b)$、$(1,0,0),(0,1,0),...$）。
      // 单元组由本规则承接，多元组列表（几何题里高频的顶点坐标列表）也由它承接 ——
      // 走独立判定避免 looksLikeNakedMath 的 length ≤ 20 上限把长顶点列表卡掉。
      if (looksLikeMathTupleList(inner.trim())) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      // 再豁免"含运算符 / 函数调用形态"的短裸表达式（$Ax=b$、$r(A)=2$、$f(x)$、$|x|<1$）。
      // 这条专治题库里没写宏的真公式被 SHELL_TRIGGER 误伤进而级联吃掉邻居 $\alpha$ 的场景。
      if (looksLikeNakedMath(inner)) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      // 再做一道"上下文加成"：如果当前 $ 所在的段落里已经出现明显 LaTeX 信号
      //（\frac / \Sigma / \begin / ^{ / _{ 等），就对短字母数字 token 也按公式保留。
      // 专治高数教材里 $xOy$/$yOz$/$zOx$ 坐标面记号、几何题里 $AB$/$ABCD$ 多顶点
      // 记号——这类形态全字母无运算符，三层"短表达式 / 列表 / 元组"豁免都没法救，
      // 但所在段落明明已经在写公式，理应按公式渲染。
      // 这条故意不替代前面五层豁免，而是作为它们的"上下文兜底"，因此放到最后；
      // 在不含 LaTeX 信号的纯文档 / 纯 Shell 段里完全不生效，$HOME$/$PATH$ 仍走老规则。
      if (isMathRichAt(i) && MATH_TOKEN_RICH_RE.test(inner.trim())) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      const next = masked[i + 1]
      if (next && SHELL_TRIGGER_RE.test(next)) {
        // 形如 $X ... $Y：开头是 Shell 模式，只转义当前 $，留给后续 $ 自己判定
        out.push(ESC)
        i++
        continue
      }
      // 普通非公式配对（next 不是 Shell 触发字符的形态：$中文$、$ 空格 $、$,abc$ 等）。
      // 之前是 push(ESC, inner, ESC) 把闭合 $ 一并吞掉，但这会把下游真公式的开 $ 拉走
      // 造成级联崩溃 —— 例如题解里 "$foo$ 后面 $\frac{1}{6}$"，$foo$ 不是公式但 $ 应单独
      // 转义，否则闭合 $ 会跨段配 $\frac{1}{6}$ 的开 $。
      // 改成只转义当前 $，把闭合 $ 留下让它自己做下一轮判定（与上方 SHELL_TRIGGER 分支一致）。
      out.push(ESC)
      i++
      continue
    }

    // 落单的 $
    out.push(ESC)
    i++
  }

  return unmask(out.join(''), masks)
}

export default escapeNonMathDollars
